if (!customElements.get('cart-drawer-upsells')) {
  class CartDrawerUpsells extends HTMLElement {
    constructor() {
      super();
      this.activeItem = null;
      this.activeVariants = [];
      this.activeSwatches = {};
      this.currentSelectedColor = '';
      this.currentSelectedSize = '';
    }

    connectedCallback() {
      this.sheet = this.querySelector('.cart-drawer-upsells__sheet');
      this.backdrop = this.querySelector('.cart-drawer-upsells__sheet-backdrop');
      this.initEvents();
    }

    initEvents() {
      this.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-action="add-to-cart"]');
        if (addBtn) {
          e.preventDefault();
          this.handleAdd(addBtn);
          return;
        }

        const pillTrigger = e.target.closest('[data-action="open-sheet"]');
        if (pillTrigger) {
          e.preventDefault();
          const item = pillTrigger.closest('.cart-drawer-upsell-item, .cart-drawer-upsell-card');
          if (item) this.openSheet(item);
          return;
        }
      });

      const handleSheetClick = (e) => {
        const closeBtn = e.target.closest('[data-action="close-sheet"]');
        if (closeBtn) {
          e.preventDefault();
          this.closeSheet();
          return;
        }

        const applyBtn = e.target.closest('[data-action="apply-sheet"]');
        if (applyBtn) {
          e.preventDefault();
          this.closeSheet();
          return;
        }

        const imgWrap = e.target.closest('.sheet-preview-img-wrap');
        if (imgWrap) {
          e.preventDefault();
          imgWrap.classList.toggle('is-full-view');
          return;
        }

        const swatchBtn = e.target.closest('.sheet-swatch-circle');
        if (swatchBtn) {
          e.preventDefault();
          this.handleSelection(swatchBtn);
          return;
        }

        const sizeBtn = e.target.closest('.sheet-size-pill, .sheet-pill');
        if (sizeBtn) {
          e.preventDefault();
          this.handleSelection(sizeBtn);
          return;
        }
      };

      if (this.sheet) this.sheet.addEventListener('click', handleSheetClick);
      if (this.backdrop) this.backdrop.addEventListener('click', handleSheetClick);
    }

    async handleAdd(btn) {
      const item = btn.closest('.cart-drawer-upsell-item, .cart-drawer-upsell-card');
      const variantId = btn.dataset.variantId || item?.dataset.variantId;

      if (!variantId || btn.disabled) return;

      btn.classList.add('is-loading');
      btn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: 1 }],
            sections: 'cart-drawer-section'
          })
        });

        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.description || 'Failed to add to cart');
        }

        const data = await response.json();

        if (data.sections && data.sections['cart-drawer-section']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data.sections['cart-drawer-section'], 'text/html');

          const newContent = doc.querySelector('.cart-drawer__content');
          const currentContent = document.querySelector('.cart-drawer__content');

          if (newContent && currentContent) {
            currentContent.replaceWith(newContent);
          }

          // Update cart badge if present
          const newBadge = doc.querySelector('.theme-drawer__badge');
          const currentBadge = document.querySelector('.theme-drawer__badge');
          if (newBadge && currentBadge) {
            currentBadge.replaceWith(newBadge);
          }

          // Trigger standard cart update event
          document.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: data }
          }));
        } else {
          // Fallback: fetch cart drawer section
          const sectionRes = await fetch('/cart?section_id=cart-drawer-section');
          if (sectionRes.ok) {
            const html = await sectionRes.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newContent = doc.querySelector('.cart-drawer__content');
            const currentContent = document.querySelector('.cart-drawer__content');
            if (newContent && currentContent) {
              currentContent.replaceWith(newContent);
            }
          }
        }
      } catch (error) {
        console.error('Cart upsell add error:', error);
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    }

    getSwatchBackground(colorName) {
      if (this.activeSwatches && this.activeSwatches[colorName]) {
        return this.activeSwatches[colorName];
      }
      const normalized = (colorName || '').trim().toLowerCase();
      const colorMap = {
        'watermelon': 'rgb(206, 96, 89)',
        'bay': 'rgb(188, 200, 190)',
        'berry': 'rgb(128, 80, 104)',
        'blue jean': 'rgb(96, 104, 126)',
        'grey': '#7f8589',
        'gray': '#7f8589',
        'ivory': '#f5f0e1',
        'terracotta': '#c46b51',
        'espresso': '#635147',
        'black': '#222222',
        'white': '#ffffff',
        'navy': '#1b2a4a',
        'pepper': '#4a4845',
        'moss': '#6b7a5a',
        'sage': '#9caf88',
        'banana': '#f3e5ab',
        'crimson': '#990000',
        'yam': '#cc7a00',
        'violet': '#7f00ff'
      };
      return colorMap[normalized] || normalized;
    }

    openSheet(item) {
      this.activeItem = item;

      const dialog = document.querySelector('theme-drawer#cart-drawer dialog') || this;
      if (this.sheet && this.sheet.parentElement !== dialog) {
        dialog.appendChild(this.backdrop);
        dialog.appendChild(this.sheet);
      }

      const rawData = item.querySelector('.upsell-variants-data')?.textContent;
      try {
        const parsed = JSON.parse(rawData || '[]');
        if (Array.isArray(parsed)) {
          this.activeVariants = parsed;
          this.activeSwatches = {};
        } else {
          this.activeVariants = parsed.variants || [];
          this.activeSwatches = parsed.swatches || {};
        }
      } catch (err) {
        this.activeVariants = [];
        this.activeSwatches = {};
      }

      this.currentSelectedColor = item.dataset.selectedColor || '';
      this.currentSelectedSize = item.dataset.selectedSize || '';

      // Find current selected variant
      let currentVariant = this.activeVariants.find(v => v.id == item.dataset.variantId);
      if (!currentVariant) {
        currentVariant = this.activeVariants.find(v => v.option1 === this.currentSelectedColor && v.option2 === this.currentSelectedSize);
      }
      if (!currentVariant && this.activeVariants.length > 0) {
        currentVariant = this.activeVariants[0];
      }

      // Populate sheet preview header
      const previewImg = this.sheet.querySelector('.sheet-preview-img');
      const imgWrap = this.sheet.querySelector('.sheet-preview-img-wrap');
      if (imgWrap) imgWrap.classList.remove('is-full-view');
      const previewTitle = this.sheet.querySelector('.sheet-preview-title');
      const previewPrice = this.sheet.querySelector('.sheet-preview-price');
      const previewCompare = this.sheet.querySelector('.sheet-preview-compare-price');

      const itemImg = item.querySelector('.cart-drawer-upsell-item__image, .cart-drawer-upsell-card__image');
      const itemTitle = item.querySelector('.cart-drawer-upsell-item__title, .cart-drawer-upsell-card__title');

      if (previewImg) {
        previewImg.src = currentVariant?.image || itemImg?.src || '';
      }
      if (previewTitle && itemTitle) {
        previewTitle.textContent = itemTitle.textContent.trim();
      }
      if (previewPrice && currentVariant) {
        previewPrice.textContent = currentVariant.price_formatted;
      }
      if (previewCompare && currentVariant) {
        if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
          previewCompare.textContent = currentVariant.compare_at_price_formatted;
          previewCompare.style.display = 'inline';
        } else {
          previewCompare.textContent = '';
          previewCompare.style.display = 'none';
        }
      }

      // Extract unique colors and sizes
      const colors = [...new Set(this.activeVariants.map(v => v.option1).filter(Boolean))];
      const sizes = [...new Set(this.activeVariants.map(v => v.option2).filter(Boolean))];

      // Render circular color swatches
      const colorsList = this.sheet.querySelector('.sheet-colors-list');
      const colorGroup = this.sheet.querySelector('.sheet-color-group');
      const selectedColorText = this.sheet.querySelector('.sheet-selected-color-text');

      if (colors.length > 0) {
        colorGroup.style.display = 'flex';
        if (selectedColorText) selectedColorText.textContent = this.currentSelectedColor;

        colorsList.innerHTML = colors.map(color => {
          const isActive = color === this.currentSelectedColor;
          const swatchBg = this.getSwatchBackground(color);
          return `<button type="button" class="sheet-swatch-circle ${isActive ? 'is-active' : ''}" data-type="color" data-value="${this.escapeHtml(color)}" aria-label="${this.escapeHtml(color)}" title="${this.escapeHtml(color)}" style="--swatch-bg: ${swatchBg};"><span class="sheet-swatch-inner"></span></button>`;
        }).join('');
      } else {
        colorGroup.style.display = 'none';
      }

      // Render larger size pills
      const sizesList = this.sheet.querySelector('.sheet-sizes-list');
      const sizeGroup = this.sheet.querySelector('.sheet-size-group');
      const selectedSizeText = this.sheet.querySelector('.sheet-selected-size-text');

      if (sizes.length > 0) {
        sizeGroup.style.display = 'flex';
        if (selectedSizeText) selectedSizeText.textContent = this.currentSelectedSize;

        sizesList.innerHTML = sizes.map(size => {
          const isActive = size === this.currentSelectedSize;
          const variantForSize = this.activeVariants.find(v => v.option1 === this.currentSelectedColor && v.option2 === size);
          const isAvailable = variantForSize ? variantForSize.available : true;
          return `<button type="button" class="sheet-size-pill ${isActive ? 'is-active' : ''} ${!isAvailable ? 'is-sold-out' : ''}" data-type="size" data-value="${this.escapeHtml(size)}">${size}</button>`;
        }).join('');
      } else {
        sizeGroup.style.display = 'none';
      }

      this.sheet.classList.add('is-open');
      this.backdrop.classList.add('is-open');
    }

    closeSheet() {
      if (this.sheet) this.sheet.classList.remove('is-open');
      if (this.backdrop) this.backdrop.classList.remove('is-open');
    }

    handleSelection(target) {
      const type = target.dataset.type;
      const val = target.dataset.value;

      if (type === 'color') {
        this.currentSelectedColor = val;
        const colorLabel = this.sheet.querySelector('.sheet-selected-color-text');
        if (colorLabel) colorLabel.textContent = val;

        this.sheet.querySelectorAll('.sheet-colors-list .sheet-swatch-circle').forEach(btn => {
          btn.classList.toggle('is-active', btn.dataset.value === val);
        });

        // Update sold-out state on size pills according to the newly selected color
        this.sheet.querySelectorAll('.sheet-sizes-list .sheet-size-pill').forEach(btn => {
          const s = btn.dataset.value;
          const v = this.activeVariants.find(varObj => varObj.option1 === val && varObj.option2 === s);
          const avail = v ? v.available : false;
          btn.classList.toggle('is-sold-out', !avail);
        });
      } else if (type === 'size') {
        this.currentSelectedSize = val;
        const sizeLabel = this.sheet.querySelector('.sheet-selected-size-text');
        if (sizeLabel) sizeLabel.textContent = val;

        this.sheet.querySelectorAll('.sheet-sizes-list .sheet-size-pill, .sheet-sizes-list .sheet-pill').forEach(btn => {
          btn.classList.toggle('is-active', btn.dataset.value === val);
        });
      }

      // Find matching variant
      let matched = this.activeVariants.find(v => v.option1 === this.currentSelectedColor && v.option2 === this.currentSelectedSize);
      if (!matched) {
        matched = this.activeVariants.find(v => (v.option1 === this.currentSelectedColor || v.option2 === this.currentSelectedSize) && v.available);
      }
      if (!matched) {
        matched = this.activeVariants.find(v => v.option1 === this.currentSelectedColor) || this.activeVariants[0];
      }

      if (matched) {
        // 1. Immediately update Sheet Preview image and prices so user sees the change in real-time
        const previewImg = this.sheet.querySelector('.sheet-preview-img');
        const previewPrice = this.sheet.querySelector('.sheet-preview-price');
        const previewCompare = this.sheet.querySelector('.sheet-preview-compare-price');

        if (previewImg && matched.image) {
          previewImg.src = matched.image;
        }
        if (previewPrice && matched.price_formatted) {
          previewPrice.textContent = matched.price_formatted;
        }
        if (previewCompare) {
          if (matched.compare_at_price && matched.compare_at_price > matched.price) {
            previewCompare.textContent = matched.compare_at_price_formatted;
            previewCompare.style.display = 'inline';
          } else {
            previewCompare.textContent = '';
            previewCompare.style.display = 'none';
          }
        }

        // 2. Synchronize the upsell item card in the cart drawer
        if (this.activeItem) {
          this.activeItem.dataset.variantId = matched.id;
          this.activeItem.dataset.selectedColor = matched.option1 || this.currentSelectedColor;
          this.activeItem.dataset.selectedSize = matched.option2 || this.currentSelectedSize;

          const itemImg = this.activeItem.querySelector('.cart-drawer-upsell-item__image, .cart-drawer-upsell-card__image');
          if (itemImg && matched.image) itemImg.src = matched.image;

          const itemPrice = this.activeItem.querySelector('.cart-drawer-upsell-item__price, .cart-drawer-upsell-card__price');
          if (itemPrice && matched.price_formatted) itemPrice.textContent = matched.price_formatted;

          const itemCompare = this.activeItem.querySelector('.cart-drawer-upsell-item__compare-price');
          if (itemCompare) {
            if (matched.compare_at_price && matched.compare_at_price > matched.price) {
              itemCompare.textContent = matched.compare_at_price_formatted;
              itemCompare.style.display = 'inline';
            } else {
              itemCompare.textContent = '';
              itemCompare.style.display = 'none';
            }
          }

          // Update minimal variant pill text: e.g. "Watermelon / L"
          const pillText = this.activeItem.querySelector('.pill-variant-text');
          if (pillText) {
            const parts = [matched.option1, matched.option2].filter(Boolean);
            pillText.textContent = parts.join(' / ');
          }

          // Update Add button
          const addBtn = this.activeItem.querySelector('[data-action="add-to-cart"]');
          if (addBtn) {
            addBtn.dataset.variantId = matched.id;
            addBtn.disabled = !matched.available;
            const textSpan = addBtn.querySelector('.btn-text');
            if (textSpan) {
              textSpan.textContent = matched.available ? '+ Add' : 'Sold Out';
            }
          }
        }
      }
    }

    escapeHtml(str) {
      return (str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  }

  customElements.define('cart-drawer-upsells', CartDrawerUpsells);
}
